import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useDeleteMaterialMutation } from '@/features/workorders/workorders.hooks';
import { WorkorderMaterial } from '@certhon/models';
import { Lock, MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react';
import { StackLink } from '@/app/navigation/StackLink';

interface WorkorderMaterialsTableProps {
  workorderRecnum: number;
  /** Path of the workorder details panel; entry links are built below it. */
  basePath: string;
  materials: WorkorderMaterial[];
  isLoading: boolean;
  isLocked?: boolean;
}

export function WorkorderMaterialsTable({
  workorderRecnum,
  basePath,
  materials,
  isLoading,
  isLocked: workorderLocked = false,
}: WorkorderMaterialsTableProps) {
  const deleteMutation = useDeleteMaterialMutation(workorderRecnum);

  const handleDelete = (recnum: number) => {
    if (confirm('Are you sure you want to delete this material?')) {
      deleteMutation.mutate(recnum);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!workorderLocked && (
        <div className="flex justify-end">
          <Button asChild size="sm" tooltip={false}>
            <StackLink to={`${basePath}/materials/new`}>
              <Plus className="mr-1 h-4 w-4" />
              Add Material
            </StackLink>
          </Button>
        </div>
      )}

      {materials.length === 0 ? (
        <div className="text-muted-foreground py-8 text-center">
          No materials yet
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Article</TableHead>
              <TableHead className="w-24">Quantity</TableHead>
              <TableHead className="w-24">Unit</TableHead>
              <TableHead className="w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {materials.map((material) => {
              const isEntryLocked =
                material.locked || material.source === 'pre' || workorderLocked;

              return (
                <TableRow
                  key={material.recnum}
                  className={isEntryLocked ? 'bg-muted/50' : undefined}
                >
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {(material.locked || material.source === 'pre') && (
                        <Lock className="text-muted-foreground h-3 w-3 flex-shrink-0" />
                      )}
                      <span
                        className={isEntryLocked ? 'text-muted-foreground' : ''}
                      >
                        {material.text || '—'}
                      </span>
                      {material.source === 'pre' && (
                        <span className="text-muted-foreground text-xs">
                          (pre-configured)
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{material.quantity ?? '—'}</TableCell>
                  <TableCell>{material.unit || '—'}</TableCell>
                  <TableCell>
                    {!isEntryLocked && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <StackLink
                              to={`${basePath}/materials/${material.recnum}`}
                            >
                              <Pencil className="mr-2 h-4 w-4" />
                              Edit
                            </StackLink>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDelete(material.recnum)}
                            className="text-destructive"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
